import type { ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { Home, Search, ShoppingCart, User, type LucideIcon } from "lucide-react";
import logoMark from "../../images/logo-mark.png";
import { useCustomerSession } from "../../routes/account/session";
import { useCart } from "../../routes/cart/useCart";
import { Icon } from "../ui";
import { cx } from "../ui/cx";
import { SearchBox, searchPath } from "./SearchBox";

const NAV = [
  { to: "/equipment-categories", label: "Browse machines" },
  { to: "/product-types", label: "Supplies by type" },
];

/**
 * The customer frame: sticky paper header, the page, a quiet footer. On a phone, search, the
 * basket and the account move to a bottom tab bar (mobile kit Phone), clear of the page's end.
 * Checkout puts its own pinned total and Place order bar there instead, as the big shops do (KALI-24).
 */
export function StoreShell({ children }: { children: ReactNode }) {
  const checkout = useLocation().pathname === "/checkout";
  return (
    <div
      className={cx(
        "flex min-h-dvh flex-col bg-surface-page [--tab-bar-height:64px]",
        checkout ? "pb-[calc(96px+env(safe-area-inset-bottom))] lg:pb-0" : "pb-[calc(var(--tab-bar-height)+env(safe-area-inset-bottom))] md:pb-0",
      )}
    >
      <header className="sticky top-0 z-30 border-b border-border-subtle bg-[rgba(247,245,234,.94)] shadow-sm [backdrop-filter:var(--blur-panel)]">
        <div className="mx-auto flex max-w-(--container-xl) flex-wrap items-center gap-x-8 gap-y-2 px-4 py-2 sm:px-6 md:h-[68px] md:flex-nowrap md:py-0">
          <Link to="/" className="order-1 flex items-center gap-3 border-b-0" aria-label="Kalikaya parts catalogue, home">
            <img src={logoMark} alt="" width={49} height={46} className="h-9 w-auto md:h-[46px]" />
            <span className="flex flex-col gap-0.5">
              <span className="bg-(image:--gradient-wordmark) bg-clip-text font-display text-[19px] leading-none font-extrabold tracking-[-0.01em] text-transparent md:text-[23px]">
                KALIKAYA
              </span>
              <span className="hidden type-eyebrow text-[8px] tracking-[0.13em] text-ink-500 md:block">ONLINE PARTS CATALOGUE</span>
            </span>
          </Link>
          <HeaderLink kind="account" />
          <nav className="order-3 flex w-full gap-6 md:order-2 md:w-auto md:gap-7">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cx(
                    "flex min-h-11 items-center border-b-2 type-h4 [font-size:var(--text-base)] whitespace-nowrap md:min-h-0 md:pb-0.5",
                    isActive ? "border-gold-500 text-text-strong" : "border-transparent text-text-body hover:text-text-strong",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <HeaderSearch />
          <HeaderLink kind="basket" />
        </div>
        <CompanyPricing />
      </header>
      <div className="flex-1 pb-16">{children}</div>
      <footer className="border-t border-border-subtle">
        <div className="mx-auto flex max-w-(--container-xl) flex-wrap justify-between gap-x-6 gap-y-3 px-4 py-6 type-small text-text-muted sm:px-6">
          <span>Kalikaya · Agri products &amp; spare parts</span>
          <nav aria-label="Help" className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/contact">Contact us</Link>
            <Link to="/delivery">Delivery</Link>
            <Link to="/returns">Cancellations and returns</Link>
          </nav>
          <span className="w-full">Prices include tax. Delivery is quoted separately.</span>
        </div>
      </footer>
      {!checkout && <TabBar />}
    </div>
  );
}

/** Search from any page, on a wide screen. Hidden on the results page and the home page, which have their own bigger box. */
function HeaderSearch() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (pathname === "/search" || pathname === "/") return <span className="hidden md:order-3 md:ml-auto md:block" />;
  return <SearchBox onSearch={(query) => navigate(searchPath(query))} className="hidden md:order-3 md:ml-auto md:flex md:w-[380px]" />;
}

/**
 * A corporate account's login sees its own prices everywhere, and must be able to tell they
 * are its prices, not the public ones (PRD 5.11). Nobody else ever sees this band.
 */
function CompanyPricing() {
  const company = useCustomerSession().data?.user?.company;
  if (!company) return null;
  return (
    <div className="border-t border-blue-200 bg-surface-tint-blue">
      <p className="mx-auto mb-0 max-w-(--container-xl) px-4 py-1.5 type-eyebrow text-[11px] tracking-[var(--tracking-wider)] text-info-700 uppercase sm:px-6">
        Company prices for {company.name} · {company.discount_percent}% off every part
      </p>
    </div>
  );
}

/** "Sign in" or "Account", and where each tab goes. A signed-out visitor is brought back after signing in. */
function useAccountTarget() {
  const session = useCustomerSession();
  const { pathname, search } = useLocation();
  const signedIn = Boolean(session.data?.user);
  return {
    to: signedIn ? "/account" : "/sign-in",
    state: signedIn ? undefined : { from: pathname + search },
    label: signedIn ? "Account" : "Sign in",
    pending: session.isPending,
  };
}

/** How many pieces are in the basket; nothing while it loads. */
function useBasketCount() {
  return useCart().data?.item_count ?? 0;
}

/** Wide screens: the account and the basket, after the search box. On a phone they're tabs. */
function HeaderLink({ kind }: { kind: "account" | "basket" }) {
  const account = useAccountTarget();
  const count = useBasketCount();
  const basket = kind === "basket";
  return (
    <Link
      to={basket ? "/cart" : account.to}
      state={basket ? undefined : account.state}
      className={cx(
        "hidden min-h-11 items-center gap-2 border-b-0 type-label whitespace-nowrap text-text-body hover:text-text-strong md:flex",
        basket ? "md:order-5" : "md:order-4",
        !basket && account.pending && "invisible",
      )}
    >
      <Icon icon={basket ? ShoppingCart : User} size={18} />
      {basket ? "Basket" : account.label}
      {basket && count > 0 && <CountBadge count={count} />}
    </Link>
  );
}

function CountBadge({ count, className }: { count: number; className?: string }) {
  return (
    <span
      aria-label={`${count} in the basket`}
      className={cx("flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-green-700 px-[5px] type-sku text-[11px] leading-none text-white", className)}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

type Tab = { to: string; label: string; icon: LucideIcon; active: boolean; state?: unknown; badge?: number };

/** The phone's bottom tabs (mobile kit Phone): shop, search, basket, account. */
function TabBar() {
  const { pathname } = useLocation();
  const account = useAccountTarget();
  const count = useBasketCount();
  const accountPaths = ["/account", "/orders", "/sign-in"];
  const tabs: Tab[] = [
    { to: "/search", label: "Search", icon: Search, active: pathname === "/search" },
    { to: "/cart", label: "Basket", icon: ShoppingCart, active: pathname === "/cart" || pathname === "/checkout", badge: count },
    { to: account.to, label: account.label, icon: User, active: accountPaths.some((path) => pathname.startsWith(path)), state: account.state },
  ];
  const shop: Tab = { to: "/", label: "Shop", icon: Home, active: !tabs.some((tab) => tab.active) };
  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border-subtle bg-surface-card pb-[env(safe-area-inset-bottom)] shadow-[0_-1px_3px_rgba(28,63,40,.06)] md:hidden"
    >
      {[shop, ...tabs].map((tab) => (
        <Link
          key={tab.label}
          to={tab.to}
          state={tab.state}
          aria-current={tab.active ? "page" : undefined}
          className={cx(
            "flex h-(--tab-bar-height) flex-1 flex-col items-center justify-center gap-1 border-b-0",
            tab.active ? "text-green-800" : "text-ink-400",
          )}
        >
          <span className="relative flex">
            <Icon icon={tab.icon} size={22} />
            {tab.badge ? <CountBadge count={tab.badge} className="absolute -top-1.5 -right-2.5" /> : null}
          </span>
          <span className="type-eyebrow text-[11px] tracking-[var(--tracking-wide)] uppercase">{tab.label}</span>
        </Link>
      ))}
    </nav>
  );
}

/** Page width and gutters: 16px on a phone, 24px from 640px. */
export function PageWidth({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("mx-auto w-full max-w-(--container-xl) px-4 sm:px-6", className)}>{children}</div>;
}
